import { useState, useEffect, useRef } from "react";
import logoHuidanalyse from "@/assets/logo-huidanalyse.png";
import { motion, AnimatePresence } from "framer-motion";
import ProgressBar from "@/components/funnel/ProgressBar";
import QuestionConcern from "@/components/funnel/QuestionConcern";
import QuestionProducts from "@/components/funnel/QuestionProducts";
import InsightValidation from "@/components/funnel/InsightValidation";
import QuestionSatisfaction from "@/components/funnel/QuestionSatisfaction";
import QuestionClinic from "@/components/funnel/QuestionClinic";
import SkinScience from "@/components/funnel/SkinScience";
import QuestionAge from "@/components/funnel/QuestionAge";
import CollagenGraph from "@/components/funnel/CollagenGraph";
import PersonalProfile from "@/components/funnel/PersonalProfile";
import ProductMatch from "@/components/funnel/ProductMatch";
import Investment from "@/components/funnel/Investment";
import ProductFailure from "@/components/funnel/ProductFailure";
import QuestionDream from "@/components/funnel/QuestionDream";
import IngredientsMatch from "@/components/funnel/IngredientsMatch";
import { useQuizTracking } from "@/hooks/useQuizTracking";

export interface UserData {
  concern: string;
  productsTried: string[];
  satisfaction: number;
  consideredClinic: string;
  age: string;
  skinConcerns: string[];
}

const TOTAL_STEPS = 14;

const pageVariants = {
  enter: { opacity: 0, x: 50 },
  center: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -50 },
};

// Save UTM params to sessionStorage immediately on module load (not in useEffect)
// This ensures UTMs are captured synchronously before any component renders
;(function captureAndStoreUTMs() {
  try {
    const params = new URLSearchParams(window.location.search);
    const keys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
    keys.forEach(key => {
      const val = params.get(key);
      if (val) sessionStorage.setItem(key, val);
    });
  } catch {
    // sessionStorage not available (private mode edge case)
  }
})();

const QUIZ_WEBHOOK_URL = 'https://ejepfwphnjtvoegtswjd.supabase.co/functions/v1/quiz-webhook?quiz=huidanalyse';

const Index = () => {
  const [step, setStep] = useState(0);
  const [userData, setUserData] = useState<UserData>({
    concern: "",
    productsTried: [],
    satisfaction: 5,
    consideredClinic: "",
    age: "",
    skinConcerns: [],
  });
  const completionSentRef = useRef(false);

  // Preload external images so they're cached before user reaches those steps
  useEffect(() => {
    const imagesToPreload = [
      "https://cdn.shopify.com/s/files/1/0698/6416/1465/files/4R9A2896.jpg?v=1770992952",
    ];
    imagesToPreload.forEach((src) => {
      const img = new Image();
      img.src = src;
    });
  }, []);

  const { trackAnswers, abVariant } = useQuizTracking(step, TOTAL_STEPS);

  // Send quiz completion webhook when user reaches the final result screen (step 13)
  useEffect(() => {
    if (step !== 13 || completionSentRef.current) return;
    completionSentRef.current = true;

    const sessionId = sessionStorage.getItem('quiz_session_id') || crypto.randomUUID();
    const answers = [
      { question: "Huidprobleem", answer: userData.concern },
      { question: "Leeftijd", answer: userData.age },
      { question: "Gebruikte producten", answer: userData.productsTried.join(', ') },
      { question: "Tevredenheid", answer: String(userData.satisfaction) },
      { question: "Overweegt kliniek", answer: userData.consideredClinic },
      { question: "Huidproblemen details", answer: userData.skinConcerns.join(', ') },
    ].filter(a => a.answer && a.answer.trim() !== '');

    fetch(QUIZ_WEBHOOK_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        quiz_name: 'huidanalyse',
        session_id: sessionId,
        answers,
      }),
    }).catch(() => {
      // Fire-and-forget — fouten loggen maar quiz niet blokkeren
    });
  }, [step]);

  const next = () => {
    window.scrollTo(0, 0);
    setStep((s) => Math.min(s + 1, TOTAL_STEPS - 1));
  };
  const back = () => {
    window.scrollTo(0, 0);
    setStep((s) => Math.max(s - 1, 0));
  };
  const update = (data: Partial<UserData>) => {
    const newData = { ...userData, ...data };
    setUserData(newData);
    trackAnswers(newData as unknown as Record<string, unknown>);
  };

  return (
    <main className="min-h-screen bg-gradient-warm">
      <div className="flex items-center px-4 md:px-6 py-3">
        <img src={logoHuidanalyse} alt="Huidanalyse" className="h-8 md:h-9" />
      </div>
      <ProgressBar currentStep={step} totalSteps={TOTAL_STEPS} />

      <div className="min-h-screen flex items-start md:items-center justify-center px-4 pb-8 md:px-5 md:py-16 pt-10">
        <div className="w-full max-w-xl">
          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              variants={pageVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.3, ease: "easeOut" }}
            >
              {step === 0 && <QuestionAge onNext={next} onSelect={(v) => update({ age: v })} selected={userData.age} showBadge={abVariant === "A"} />}
              {step === 1 && <QuestionConcern onNext={next} onBack={back} onSelect={(v) => update({ concern: v })} selected={userData.concern} />}
              {step === 2 && <QuestionProducts onNext={next} onBack={back} onSelect={(v) => update({ productsTried: v })} selected={userData.productsTried} />}
              {step === 3 && <InsightValidation onNext={next} productsTried={userData.productsTried} age={userData.age} />}
              {step === 4 && <QuestionSatisfaction onNext={next} onBack={back} value={userData.satisfaction} onChange={(v) => update({ satisfaction: v })} />}
              {step === 5 && <QuestionClinic onNext={next} onBack={back} onSelect={(v) => update({ consideredClinic: v })} selected={userData.consideredClinic} />}
              {step === 6 && <ProductFailure onNext={next} productsTried={userData.productsTried} />}
              {step === 7 && <SkinScience onNext={next} productsTried={userData.productsTried} />}
              {step === 8 && <QuestionDream onNext={next} onBack={back} />}
              {step === 9 && <CollagenGraph age={userData.age} concern={userData.concern} onNext={next} />}
              {step === 10 && <PersonalProfile userData={userData} onUpdate={update} onNext={next} onBack={back} />}
              {step === 11 && <IngredientsMatch concern={userData.concern} skinConcerns={userData.skinConcerns} satisfaction={userData.satisfaction} onNext={next} />}
              {step === 12 && <ProductMatch userData={userData} onNext={next} />}
              {step === 13 && <Investment userData={userData} onBack={back} />}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      {/* Pre-render upcoming screens offscreen so images/SVGs are cached and ready */}
      {/* @ts-ignore - inert is a valid HTML attribute */}
      <div aria-hidden="true" inert="" className="fixed -left-[9999px] top-0 w-[100vw] overflow-hidden pointer-events-none opacity-0">
        <div className="max-w-xl">
          {step < 1 && <QuestionConcern onNext={() => {}} onBack={() => {}} onSelect={() => {}} selected="" />}
          {step < 2 && <QuestionProducts onNext={() => {}} onBack={() => {}} onSelect={() => {}} selected={[]} />}
          {step < 3 && <InsightValidation onNext={() => {}} productsTried={[]} age="" />}
          {step < 4 && <QuestionSatisfaction onNext={() => {}} onBack={() => {}} value={5} onChange={() => {}} />}
          {step < 5 && <QuestionClinic onNext={() => {}} onBack={() => {}} onSelect={() => {}} selected="" />}
          {step < 6 && <ProductFailure onNext={() => {}} productsTried={[]} />}
          {step < 7 && <SkinScience onNext={() => {}} productsTried={[]} />}
          {step < 8 && <QuestionDream onNext={() => {}} onBack={() => {}} />}
          {step < 9 && <CollagenGraph age="" concern="" onNext={() => {}} />}
          {step < 10 && <PersonalProfile userData={userData} onUpdate={() => {}} onNext={() => {}} onBack={() => {}} />}
          {step < 11 && <IngredientsMatch concern="" skinConcerns={[]} satisfaction={5} onNext={() => {}} />}
          {step < 12 && <ProductMatch userData={userData} onNext={() => {}} />}
          {step < 13 && <Investment userData={userData} onBack={() => {}} />}
          {/* Pre-load the Shopify product image */}
          <img src="https://cdn.shopify.com/s/files/1/0698/6416/1465/files/4R9A2896.jpg?v=1770992952" alt="" loading="eager" />
        </div>
      </div>
    </main>
  );
};

export default Index;
